import React, { useState, useEffect } from 'react';
import { Card, Button, ButtonGroup } from 'react-bootstrap';
import { Select, DatePicker, Space, Checkbox, message, Input } from 'antd';
import { PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useFetching } from '../../hooks/useFetching';
import ReportService from '../../http/reportService';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../store';
import { fetchStores, fetchCategories } from '../../http/productAPI';
import { useNavigate, useLocation } from 'react-router-dom';
import { UNIVERSAL_REPORT_ROUTE, ADMIN_ROUTE } from '../../utils/consts';

const { RangePicker } = DatePicker;

const UniversalReport = observer(() => {
    const navigate = useNavigate();
    const location = useLocation();
    const { store } = useStore();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFields, setSelectedFields] = useState(['name', 'type', 'price']);
    const [filters, setFilters] = useState({});
    const [dateRange, setDateRange] = useState(null);
    const [groupBy, setGroupBy] = useState([]);
    const [error, setError] = useState(null);
    const [stores, setStores] = useState([]);
    const [categories, setCategories] = useState([]);
    const [reportTitle, setReportTitle] = useState('Универсальный отчет');

    // Доступные поля для отчета
    const availableFields = [
        { label: 'Название', value: 'name' },
        { label: 'Тип', value: 'type' },
        { label: 'Категория', value: 'category' },
        { label: 'Цена', value: 'price' },
        { label: 'Артикул', value: 'article' },
        { label: 'Размер', value: 'size' },
        { label: 'Цвет', value: 'color' },
        { label: 'Магазин', value: 'store' },
        { label: 'Тип оплаты', value: 'paymentType' },
        { label: 'Гендер', value: 'gender' },
        { label: 'Остаток', value: 'quantity' },
        { label: 'Остатки (₽)', value: 'stockValue' },
        { label: 'Продано', value: 'sold' },
        { label: 'Продано (₽)', value: 'soldValue' },
        { label: 'Дата добавления', value: 'created_at' },
        { label: 'Дата продажи', value: 'sale_date' },
    ];

    // Инициализация при монтировании
    useEffect(() => {
        if (store?.stores) {
            setSelectedFields(['name', 'type', 'price']);
        }
    }, [store?.stores]);

    // Автоматически добавляем поле "Дата добавления" если выбраны поля остатков
    useEffect(() => {
        const hasStockFields = selectedFields.includes('quantity') || selectedFields.includes('stockValue');
        const hasCreatedAt = selectedFields.includes('created_at');
        
        if (hasStockFields && !hasCreatedAt) {
            setSelectedFields(prev => [...prev, 'created_at']);
        }
    }, [selectedFields]);

    // Автоматически добавляем поле "Дата добавления" если выбран фильтр по дате
    useEffect(() => {
        const hasDateFilter = dateRange && dateRange.start && dateRange.end;
        const hasCreatedAt = selectedFields.includes('created_at');
        
        if (hasDateFilter && !hasCreatedAt) {
            setSelectedFields(prev => [...prev, 'created_at']);
        }
    }, [dateRange, selectedFields]);

    // Автоматически добавляем поле "Категория" если выбран фильтр по категории
    useEffect(() => {
        const hasCategoryFilter = filters.category_id;
        const hasCategory = selectedFields.includes('category');
        
        if (hasCategoryFilter && !hasCategory) {
            setSelectedFields(prev => [...prev, 'category']);
        }
    }, [filters.category_id, selectedFields]);

    // Загрузка магазинов при монтировании
    useEffect(() => {
        let cancelled = false;
        async function loadStores() {
            try {
                const stores = await fetchStores();
                if (!cancelled) {
                    setStores(stores);
                }
            } catch (e) {
                if (!cancelled) {
                    setStores([]);
                    message.error('Ошибка загрузки магазинов: ' + (e.response?.data?.message || e.message));
                    console.error('Ошибка загрузки магазинов:', e);
                }
            }
        }
        loadStores();
        return () => { cancelled = true; };
    }, []);

    // Загрузка категорий при монтировании
    useEffect(() => {
        let cancelled = false;
        async function loadCategories() {
            try {
                const categories = await fetchCategories();
                if (!cancelled) {
                    setCategories(categories);
                }
            } catch (e) {
                if (!cancelled) {
                    setCategories([]);
                    message.error('Ошибка загрузки категорий: ' + (e.response?.data?.message || e.message));
                    console.error('Ошибка загрузки категорий:', e);
                }
            }
        }
        loadCategories();
        return () => { cancelled = true; };
    }, []);

    // Получение данных отчета
    const [fetchReport, isFetching] = useFetching(async () => {
        try {
            setLoading(true);
            setError(null);
            
            if (!selectedFields?.length) {
                message.warning('Выберите хотя бы одно поле для отображения');
                return;
            }

            const response = await ReportService.getUniversalReport({
                fields: selectedFields,
                filters: filters || {},
                // Всегда запрашиваем необработанные данные, а группировку выполняем на клиенте
                groupBy: [],
                sortBy: [],
                dateRange: dateRange || null
            });

            if (response?.data) {
                setData(response.data);
            } else {
                setData([]);
                message.info('Нет данных для отображения');
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                message.error('Сессия истекла. Пожалуйста, войдите снова.');
                localStorage.removeItem('token');
                window.location.reload();
            } else {
                setError(error.message || 'Ошибка при загрузке отчета');
                message.error('Ошибка при загрузке отчета');
            }
            console.error('Error fetching report:', error);
        } finally {
            setLoading(false);
        }
    });

    // Автоматически загружать отчет при открытии страницы
    useEffect(() => {
        fetchReport();
        // eslint-disable-next-line
    }, [selectedFields, filters, groupBy, dateRange]);

    // Обработчики изменений
    const handleFieldChange = (checkedValues) => {
        setSelectedFields(Array.isArray(checkedValues) ? checkedValues : []);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleDateRangeChange = (dates) => {
        if (dates && dates[0] && dates[1]) {
            setDateRange({
                start: dates[0].toISOString(),
                end: dates[1].toISOString()
            });
        } else {
            setDateRange(null);
        }
    };

    const handleGroupByChange = (value) => {
        setGroupBy(Array.isArray(value) ? value : []);
    };

    const parseNumber = (value, fallback = 0) => {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : fallback;
        }
        if (typeof value === 'string' && value.trim() !== '') {
            const normalized = value.replace(/\s/g, '').replace(',', '.');
            const parsed = Number(normalized);
            return Number.isFinite(parsed) ? parsed : fallback;
        }
        return fallback;
    };

    const getRowSoldValue = (row) => {
        if (row && row.soldValue !== undefined && row.soldValue !== null) {
            const value = parseNumber(row.soldValue);
            if (Number.isFinite(value)) return value;
        }
        const price = parseNumber(row?.price);
        const sold = parseNumber(row?.sold);
        return price * sold;
    };

    const formatCurrency = (value) => parseNumber(value).toLocaleString('ru-RU');

    // --- Группировка данных ---
    function groupData(data, groupByFields) {
        if (!groupByFields || groupByFields.length === 0) return data;
        const groupField = groupByFields[0];
        const groups = {};
        data.forEach(item => {
            const key = item[groupField] || '-';
            if (!groups[key]) groups[key] = [];
            groups[key].push(item);
        });
        // Рекурсивная группировка
        if (groupByFields.length > 1) {
            Object.keys(groups).forEach(key => {
                groups[key] = groupData(groups[key], groupByFields.slice(1));
            });
        }
        return groups;
    }

    // --- Итоги по колонкам ---
    function getTotals(data, fields) {
        const totals = {};
        if (!data || !fields) return totals;
        fields.forEach(field => {
            if (field === 'quantity') {
                totals[field] = data.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
            } else if (field === 'sold') {
                totals[field] = data.reduce((sum, row) => sum + (Number(row.sold) || 0), 0);
            } else if (field === 'stockValue') {
                totals[field] = data.reduce((sum, row) => sum + ((Number(row.price) || 0) * (Number(row.quantity) || 0)), 0);
            } else if (field === 'soldValue') {
                totals[field] = data.reduce((sum, row) => sum + getRowSoldValue(row), 0);
            } else if (field === 'price') {
                totals[field] = data.reduce((sum, row) => sum + (Number(row.price) || 0), 0);
            }
        });
        return totals;
    }

    // --- Рендер группированной таблицы ---
    function renderGroupedTable(groups, groupByFields, level = 0) {
        if (!groupByFields.length) return null;
        const field = groupByFields[0];
        return Object.entries(groups).map(([key, value]) => {
            const rows = Array.isArray(value) ? value : null;
            const subGroups = !rows ? value : null;
            return (
                <React.Fragment key={key + level}>
                    <tr className={`group-row group-level-${level}`}> 
                        <td colSpan={selectedFields.length} style={{background:'#f6f6f6',fontWeight:600}}>
                            {availableFields.find(f=>f.value===field)?.label || field}: <b>{key}</b>
                        </td>
                    </tr>
                    {rows && rows.map((row, idx) => (
                        <tr key={idx}>
                            {selectedFields.map(col => {
                                if (col === 'stockValue') return <td key={col}>{formatCurrency((Number(row.price)||0)*(Number(row.quantity)||0))}</td>;
                                if (col === 'soldValue') return <td key={col}>{formatCurrency(getRowSoldValue(row))}</td>;
                                return <td key={col}>{row[col] || row[col+"Name"] || '-'}</td>;
                            })}
                        </tr>
                    ))}
                    {subGroups && renderGroupedTable(subGroups, groupByFields.slice(1), level+1)}
                    {/* Подытог по группе */}
                    {rows && (
                        <tr className={`group-total group-level-${level}`}> 
                            {selectedFields.map(col => {
                                if (col === 'quantity') return <td key={col} style={{fontWeight:700,background:'#f0f0f0'}}>{rows.reduce((sum, r) => sum + (Number(r.quantity)||0), 0)}</td>;
                                if (col === 'sold') return <td key={col} style={{fontWeight:700,background:'#f0f0f0'}}>{rows.reduce((sum, r) => sum + (Number(r.sold)||0), 0)}</td>;
                                if (col === 'stockValue') return <td key={col} style={{fontWeight:700,background:'#f0f0f0'}}>{formatCurrency(rows.reduce((sum, r) => sum + ((Number(r.price)||0)*(Number(r.quantity)||0)), 0))}</td>;
                                if (col === 'soldValue') return <td key={col} style={{fontWeight:700,background:'#f0f0f0'}}>{formatCurrency(rows.reduce((sum, r) => sum + getRowSoldValue(r), 0))}</td>;
                                return <td key={col} style={{fontWeight:700,background:'#f0f0f0'}}>{col===field?'Итого':''}</td>;
                            })}
                        </tr>
                    )}
                </React.Fragment>
            );
        });
    }

    // --- Итоговая строка ---
    function renderGrandTotal(data) {
        const totals = getTotals(data, selectedFields);
        return (
            <tr className="grand-total-row">
                {selectedFields.map(col => {
                    if (col === 'quantity') return <td key={col} style={{fontWeight:900,background:'#e0e0e0'}}>{totals[col]}</td>;
                    if (col === 'sold') return <td key={col} style={{fontWeight:900,background:'#e0e0e0'}}>{totals[col]}</td>;
                    if (col === 'stockValue') return <td key={col} style={{fontWeight:900,background:'#e0e0e0'}}>{formatCurrency(totals[col])}</td>;
                    if (col === 'soldValue') return <td key={col} style={{fontWeight:900,background:'#e0e0e0'}}>{formatCurrency(totals[col])}</td>;
                    return <td key={col} style={{fontWeight:900,background:'#e0e0e0'}}>{col==='name'?'Итого':''}</td>;
                })}
            </tr>
        );
    }

    // Печать отчета
    const handlePrint = () => {
        if (!data?.length) {
            message.warning('Нет данных для печати');
            return;
        }
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            message.error('Не удалось открыть окно печати');
            return;
        }
        // Формируем HTML для печати с учетом группировки и итогов
        let tableHtml = `<table><thead><tr>${selectedFields.map(col=>`<th>${availableFields.find(f=>f.value===col)?.label||col}</th>`).join('')}</tr></thead><tbody>`;
        if (groupBy.length) {
            const grouped = groupData(data, groupBy);
            const renderGroupRows = (groups, groupByFields, level=0) => {
                if (!groupByFields.length) return '';
                const field = groupByFields[0];
                return Object.entries(groups).map(([key, value]) => {
                    const rows = Array.isArray(value) ? value : null;
                    const subGroups = !rows ? value : null;
                    let html = `<tr style='background:#f6f6f6;font-weight:600'><td colspan='${selectedFields.length}'>${availableFields.find(f=>f.value===field)?.label||field}: <b>${key}</b></td></tr>`;
                    if (rows) {
                        html += rows.map(row => `<tr>${selectedFields.map(col=>{
                            if (col==='stockValue') return `<td>${formatCurrency((Number(row.price)||0)*(Number(row.quantity)||0))}</td>`;
                            if (col==='soldValue') return `<td>${formatCurrency(getRowSoldValue(row))}</td>`;
                            return `<td>${row[col]||row[col+"Name"]||'-'}</td>`;
                        }).join('')}</tr>`).join('');
                        // Подытог
                        html += `<tr style='font-weight:700;background:#f0f0f0'>${selectedFields.map(col=>{
                            if (col==='quantity') return rows.reduce((sum, r) => sum + (Number(r.quantity)||0), 0);
                            if (col==='sold') return rows.reduce((sum, r) => sum + (Number(r.sold)||0), 0);
                            if (col==='stockValue') return formatCurrency(rows.reduce((sum, r) => sum + ((Number(r.price)||0)*(Number(r.quantity)||0)), 0));
                            if (col==='soldValue') return formatCurrency(rows.reduce((sum, r) => sum + getRowSoldValue(r), 0));
                            return col===field?'Итого':'';
                        }).map(val=>`<td>${val}</td>`).join('')}</tr>`;
                    }
                    if (subGroups) html += renderGroupRows(subGroups, groupByFields.slice(1), level+1);
                    return html;
                }).join('');
            };
            tableHtml += renderGroupRows(grouped, groupBy);
        } else {
            tableHtml += data.map(row => `<tr>${selectedFields.map(col=>{
                if (col==='stockValue') return `<td>${formatCurrency((Number(row.price)||0)*(Number(row.quantity)||0))}</td>`;
                if (col==='soldValue') return `<td>${formatCurrency(getRowSoldValue(row))}</td>`;
                return `<td>${row[col]||row[col+"Name"]||'-'}</td>`;
            }).join('')}</tr>`).join('');
        }
        // Итоговая строка
        const totals = getTotals(data, selectedFields);
        tableHtml += `<tr style='font-weight:900;background:#e0e0e0'>${selectedFields.map(col=>{
            if (col==='quantity') return totals[col];
            if (col==='sold') return totals[col];
            if (col==='stockValue') return formatCurrency(totals[col]);
            if (col==='soldValue') return formatCurrency(totals[col]);
            return col==='name'?'Итого':'';
        }).map(val=>`<td>${val}</td>`).join('')}</tr>`;
        tableHtml += '</tbody></table>';
        printWindow.document.write(`
            <html>
                <head>
                    <title>${reportTitle}</title>
                    <style>
                        table { width: 100%; border-collapse: collapse; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background-color: #f2f2f2; }
                        .group-row { background: #f6f6f6; font-weight: 600; }
                        .group-total { background: #f0f0f0; font-weight: 700; }
                        .grand-total-row { background: #e0e0e0; font-weight: 900; }
                    </style>
                </head>
                <body>
                    <h1>${reportTitle}</h1>
                    ${tableHtml}
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    // --- Навигация ---
    const isActive = (route) => {
        if (route === UNIVERSAL_REPORT_ROUTE) return location.pathname === UNIVERSAL_REPORT_ROUTE;
        if (route === ADMIN_ROUTE) {
            if (!location.search || location.search === '' || location.search === '?tab=orders') return location.pathname === ADMIN_ROUTE;
        }
        if (route === ADMIN_ROUTE + '?tab=products') return location.pathname === ADMIN_ROUTE && location.search === '?tab=products';
        if (route === ADMIN_ROUTE + '?tab=sales') return location.pathname === ADMIN_ROUTE && location.search === '?tab=sales';
        return false;
    };

    // --- Заготовки форм ---
    function renderFormHeader(type) {
        if (type === 'sold') {
            return <h2 style={{color:'#1976d2',fontWeight:900,margin:'24px 0 12px'}}>Форма "Продано"</h2>;
        }
        if (type === 'stock') {
            const hasCreatedAt = selectedFields.includes('created_at');
            return (
                <h2 style={{color:'#388e3c',fontWeight:900,margin:'24px 0 12px'}}>
                    Форма "Остатки"
                    {hasCreatedAt && <span style={{fontSize:'14px',fontWeight:'normal',color:'#666',marginLeft:'10px'}}>
                        (с датой добавления)
                    </span>}
                </h2>
            );
        }
        return null;
    }

    if (error) {
        return (
            <Card className="mb-4">
                <div className="text-center text-danger">
                    <h3>Произошла ошибка</h3>
                    <p>{error}</p>
                    <Button variant="primary" size="sm" onClick={() => setError(null)}>
                        Попробовать снова
                    </Button>
                </div>
            </Card>
        );
    }

    return (
        <div>
            <style>{`
                .admin-tab-btn {
                    padding: 2px 12px !important;
                    font-size: 13px !important;
                    height: 30px !important;
                    min-width: 80px;
                }
            `}</style>
            <ButtonGroup className="mb-4 admin-tabs" style={{width:'100%', justifyContent:'center', display:'flex', gap:8}}>
                <Button
                    className="admin-tab-btn"
                    variant={isActive(ADMIN_ROUTE) ? 'primary' : 'outline-primary'}
                    onClick={() => navigate(ADMIN_ROUTE + '?tab=orders')}
                >
                    Заказы
                </Button>
                <Button
                    className="admin-tab-btn"
                    variant={isActive(ADMIN_ROUTE + '?tab=products') ? 'primary' : 'outline-primary'}
                    onClick={() => navigate(ADMIN_ROUTE + '?tab=products')}
                >
                    Товары
                </Button>
                <Button
                    className="admin-tab-btn"
                    variant={isActive(ADMIN_ROUTE + '?tab=sales') ? 'primary' : 'outline-primary'}
                    onClick={() => navigate(ADMIN_ROUTE + '?tab=sales')}
                >
                    Продажи
                </Button>
                <Button
                    className="admin-tab-btn"
                    variant={isActive(UNIVERSAL_REPORT_ROUTE) ? 'primary' : 'outline-primary'}
                    onClick={() => navigate(UNIVERSAL_REPORT_ROUTE)}
                    disabled={isActive(UNIVERSAL_REPORT_ROUTE)}
                >
                    Универсальный отчет
                </Button>
            </ButtonGroup>
            <div className="universal-report">
                <Card title={reportTitle} className="mb-4">
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* Название отчета */}
                        <div>
                            <h3>Название отчета:</h3>
                            <Input value={reportTitle} onChange={e=>setReportTitle(e.target.value)} maxLength={100} style={{maxWidth:400}} />
                        </div>
                        {/* Выбор полей */}
                        <div>
                            <h3>Выберите поля для отображения:</h3>
                            <Checkbox.Group
                                options={availableFields}
                                value={selectedFields}
                                onChange={handleFieldChange}
                            />
                        </div>

                        {/* Фильтры */}
                        <div>
                            <h3>Фильтры:</h3>
                            <Space>
                                <Select
                                    placeholder="Тип товара"
                                    style={{ width: 200 }}
                                    onChange={(value) => handleFilterChange('type', value)}
                                    allowClear
                                >
                                    <Select.Option value="shoe">Обувь</Select.Option>
                                    <Select.Option value="clothes">Одежда</Select.Option>
                                    <Select.Option value="toy">Игрушки</Select.Option>
                                </Select>

                                <Select
                                    placeholder="Категория"
                                    style={{ width: 200 }}
                                    onChange={(value) => handleFilterChange('category_id', value)}
                                    allowClear
                                    value={filters.category_id !== undefined ? filters.category_id : undefined}
                                >
                                    {categories && categories.length > 0 ? (
                                        categories.map(category => (
                                            <Select.Option key={category.id} value={category.id}>
                                                {category.name}
                                            </Select.Option>
                                        ))
                                    ) : (
                                        <Select.Option disabled value="">Нет категорий</Select.Option>
                                    )}
                                </Select>
                                <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
                                    Фильтр по категории товара
                                </div>

                                <Select
                                    placeholder="Магазин"
                                    style={{ width: 200 }}
                                    onChange={(value) => handleFilterChange('store_id', value)}
                                    allowClear
                                    value={filters.store_id !== undefined ? filters.store_id : undefined}
                                >
                                    {stores && stores.length > 0 ? (
                                        stores.map(s => (
                                            <Select.Option key={s.id} value={s.id}>
                                                {s.name}
                                            </Select.Option>
                                        ))
                                    ) : (
                                        <Select.Option disabled value="">Нет магазинов</Select.Option>
                                    )}
                                </Select>

                                <div>
                                    <RangePicker
                                        onChange={handleDateRangeChange}
                                        placeholder={['Начало периода', 'Конец периода']}
                                    />
                                    <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
                                        Фильтр по дате добавления товара
                                    </div>
                                </div>
                            </Space>
                        </div>

                        {/* Группировка */}
                        <div>
                            <h3>Группировка:</h3>
                            <Select
                                mode="multiple"
                                placeholder="Выберите поля для группировки"
                                style={{ width: '100%' }}
                                onChange={handleGroupByChange}
                                value={groupBy}
                            >
                                {availableFields.map(field => (
                                    <Select.Option key={field.value} value={field.value}>
                                        {field.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>

                        {/* Кнопки управления */}
                        <Space>
                            <Button
                                type="primary"
                                icon={<ReloadOutlined />}
                                onClick={fetchReport}
                                loading={loading || isFetching}
                                disabled={!selectedFields?.length}
                                size="middle"
                            >
                                Обновить
                            </Button>
                            <Button
                                icon={<PrinterOutlined />}
                                onClick={handlePrint}
                                disabled={!data?.length}
                                size="middle"
                            >
                                Печать
                            </Button>
                        </Space>
                    </Space>
                </Card>

                {/* Таблица с данными */}
                <Card>
                    {/* Заголовки форм */}
                    {selectedFields.includes('sold') && renderFormHeader('sold')}
                    {selectedFields.includes('quantity') && renderFormHeader('stock')}
                    {loading || isFetching ? (
                        <div className="text-center p-4">
                            <p>Загрузка данных...</p>
                        </div>
                    ) : data?.length > 0 ? (
                        <table className="ant-table universal-table" style={{width:'100%'}}>
                            <thead>
                                <tr>
                                    {selectedFields.map(col => (
                                        <th key={col}>{availableFields.find(f=>f.value===col)?.label||col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {groupBy.length
                                    ? renderGroupedTable(groupData(data, groupBy), groupBy)
                                    : data.map((row, idx) => (
                                        <tr key={idx}>
                                            {selectedFields.map(col => {
                                                if (col === 'stockValue') return <td key={col}>{formatCurrency((Number(row.price)||0)*(Number(row.quantity)||0))}</td>;
                                                if (col === 'soldValue') return <td key={col}>{formatCurrency(getRowSoldValue(row))}</td>;
                                                return <td key={col}>{row[col]||row[col+"Name"]||'-'}</td>;
                                            })}
                                        </tr>
                                    ))}
                                {renderGrandTotal(data)}
                            </tbody>
                        </table>
                    ) : (
                        <div className="text-center p-4">
                            <p>Нет данных для отображения. Выберите параметры и нажмите "Обновить"</p>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
});

export default UniversalReport; 